/**
 * Training (ฝึกวิชา): raising a move or a neigong a level, on a clock, its cost drained from
 * cultivation every few seconds into the chat log (the rule itself is training.ts). One thing trains
 * at a time; with auto-training on, a finished level starts the next of the same thing.
 */
import { MAX_SKILL_LEVEL } from '../cultivation';
import { getNeigong } from '../data/neigong';
import { getArt, getSkill } from '../data/skills';
import { advanceTraining, newTraining, TRAIN_TICK, type TrainingState, type TrainKind } from '../training';
import { boostMult } from '../trainBoost';
import type { SystemHost, WorldSystem } from './types';

export class TrainingSystem implements WorldSystem {
  readonly name = 'training';
  /** Training is waiting for cultivation (announced once). */
  private starved = false;

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    trainSkill: (c: { id: string }) => this.trainSkill(c.id),
    trainNeigong: (c: { id: string }) => this.trainNeigong(c.id),
    trainStop: () => this.trainStop(),
    trainAuto: (c: { on: boolean }) => {
      this.h.world.progress.trainAuto = c.on;
      this.h.notice(c.on ? 'เปิดฝึกอัตโนมัติ: ฝึกขั้นถัดไปต่อเองจนตัน' : 'ปิดฝึกอัตโนมัติ');
      this.h.emit({ type: 'progressChanged' });
    },
  };

  /** Training runs on, its cost drained every few seconds into the chat log. */
  step(dt: number): void {
    const w = this.h.world;
    const t = w.progress.training;
    if (!t) return;
    const name = this.trainingName(t);
    // The group dance's buff speeds neigong training.
    const r = advanceTraining(w.progress, dt * boostMult({ dance: w.dance.trainMult(t.kind) - 1 }));
    for (const c of r.charges) this.h.log('sys', `ฝึก${name} สูญเสียค่าฝึกฝน ${c} หน่วย`);
    if (r.starved && !this.starved) this.h.notice('แต้มฝึกปรือไม่พอจ่ายค่าฝึก การฝึกหยุดรอ');
    this.starved = r.starved;
    for (const d of r.done) this.finish(d);
    const next = w.progress.training;
    if (r.done.length && next) this.h.log('sys', `ฝึกอัตโนมัติ: เริ่มฝึก${name} ขั้น ${next.level}`);
  }

  /** "คัมภีร์สัญญาเลือด" or a move's name. */
  trainingName(t: Pick<TrainingState, 'kind' | 'id'>): string {
    return t.kind === 'neigong' ? getNeigong(t.id).name : getSkill(t.id).name;
  }

  /** The level is already applied (advanceTraining); this refreshes and announces it. */
  finish(t: TrainingState): void {
    const w = this.h.world;
    if (t.kind === 'neigong') {
      if (t.id === w.progress.activeNeigong) w.refreshPlayerStats();
      w.checkTitles();
    }
    this.h.emit({ type: 'trained', what: t.kind, id: t.id, level: t.level });
    this.h.emit({ type: 'progressChanged' });
  }

  private trainSkill(id: string): void {
    const w = this.h.world;
    const owned = w.progress.arts.some((a) => getArt(a).skills.includes(id));
    if (!owned) return;
    const lv = w.skillLevel(id);
    if (lv >= MAX_SKILL_LEVEL) return this.h.notice('กระบวนท่านี้บรรลุขั้นสูงสุดแล้ว');
    this.start('skill', id, lv);
  }

  private trainNeigong(id: string): void {
    const lv = this.h.world.progress.neigong[id];
    if (lv == null) return;
    if (lv >= getNeigong(id).maxLevel) return this.h.notice('กำลังภายในนี้บรรลุขั้นสูงสุดแล้ว');
    this.start('neigong', id, lv);
  }

  /** Starts the clock; the cost is drained as it trains. One thing trains at a time. */
  private start(kind: TrainKind, id: string, lv: number): void {
    const pr = this.h.world.progress;
    if (pr.training) return this.h.notice(`กำลังฝึก${this.trainingName(pr.training)}อยู่`);
    const t = newTraining(kind, id, lv);
    const first = Math.round((t.cost * Math.min(TRAIN_TICK, t.total)) / t.total);
    if (pr.cultivation < first)
      return this.h.notice(`แต้มฝึกปรือไม่พอ (ต้องการ ${first} ทุก ${TRAIN_TICK} วินาที)`);
    pr.training = t;
    this.starved = false;
    this.h.notice(`เริ่มฝึก${this.trainingName(t)} ขั้น ${t.level} · ค่าฝึก ${t.cost} หน่วย`);
    this.h.emit({ type: 'progressChanged' });
  }

  private trainStop(): void {
    const pr = this.h.world.progress;
    const t = pr.training;
    if (!t) return;
    pr.training = null;
    this.h.notice(`หยุดฝึก${this.trainingName(t)} ค่าฝึกที่เสียไปแล้ว ${t.paid} หน่วยไม่ได้คืน`);
    this.h.emit({ type: 'progressChanged' });
  }
}
